import { Device, DeviceV2 } from './models/device.model';
import { MfaDeviceService } from './services/mfa-device.service';
/**
 * @license
 * Copyright (c) Absa Group Limited, All Rights Reserved.
 */

import { Component, OnInit, ViewEncapsulation, ViewChild, TemplateRef } from '@angular/core';

// Store
import { Store } from '@ngrx/store';

// UI Actions
import * as thresholdActions from '../../store/actions/actions';
import { ActivateDeviceComponent } from './activate-device/activate-device.component';
import { DelinkDeviceComponent } from './delink-device/delink-device.component';
import { EditDeviceComponent } from './edit-device/edit-device.component';
import { Column, iAction } from '@solar/lunar';
import { DelinkDeviceV2Component } from './delink-device-v2/delink-device-v2.component';
import { ActivateDeviceV2Component } from './activate-device-v2/activate-device-v2.component';
import { Router } from '@angular/router';

@Component({
  encapsulation: ViewEncapsulation.Emulated,
  template: require('./manage-mfa-device.component.html'),
  styles: [
    `
      .devices-heading {
        margin-top: 40px;
      }
      .device-name {
        font-size: 14px;
        text-decoration: none;
        cursor: pointer;
      }
      .device {
        color: #fa551e;
        font-size: 20px;
        font-weight: 400;
      }
      .ico-info {
        font-size: 18px;
      }
      .icon {
        display: inline-block;
        vertical-align: middle;
      }
      a.dropdown-toggle:after {
        display: none;
      }
      a.dropdown-toggle,
      .dropdown-item {
        cursor: pointer;
      }
    `,
  ],
})
export class ManageMFADeviceComponent implements OnInit {
  @ViewChild('activateDeviceComponent') activateDeviceComponentTemplate: ActivateDeviceComponent;
  @ViewChild('delinkDeviceComponent') delinkDeviceComponentTemplate: DelinkDeviceComponent;
  @ViewChild('editDeviceComponent') editDeviceComponentTemplate: EditDeviceComponent;

  @ViewChild('delinkDeviceV2Component') delinkDeviceV2ComponentTemplate: DelinkDeviceV2Component;
  @ViewChild('activateDeviceV2Component') activateDeviceV2ComponentTemplate: ActivateDeviceV2Component;

  public deviceEnrolled: boolean = false;
  public V2DeviceEnrolled: boolean = false;
  public device: Device[];
  public loading: boolean = true;
  public warning: boolean = false;

  // Feature toggle
  public manageDeviceFeature = process.env.config.feature.MFADeviceManagement.manageDeviceFeature.enabled;
  public delinkFeature = process.env.config.feature.MFADeviceManagement.manageDeviceFeature.delinkFeature.enabled;
  public editFeature = process.env.config.feature.MFADeviceManagement.manageDeviceFeature.editFeature.enabled;
  public deactivateMfaV2Feature = process.env.config.feature.MFADeviceManagement.manageDeviceFeature.deactivateMfaV2Feature.enabled;
  public activateMfaFeatureV2 = process.env.config.feature.MFADeviceManagement.manageDeviceFeature.activateMfaFeatureV2.enabled;

  // Solar Lunar table v1
  public _loading: boolean = true;
  public _rows: any[] = [];
  public _columns: Column[] = [];
  public _actions: iAction[] = []; // for row actions

  // Custom templates
  @ViewChild('lastActivityTemplate')
  public _lastActivityTemplate: TemplateRef<any>;

  @ViewChild('registeredDateTimeTemplateTemplate')
  public _registeredDateTimeTemplateTemplate: TemplateRef<any>;

  @ViewChild('statusTemplate')
  public _statusTemplate: TemplateRef<any>;

  @ViewChild('deviceNameTemplate')
  public _deviceNameTemplate: TemplateRef<any>;

  @ViewChild('enableDisableMfa')
  public _enableDisableMfa: TemplateRef<any>;

  // Solar Lunar table v2
  public _loadingV2: boolean = true;
  public _rowsV2: DeviceV2[] = [];
  public _columnsV2: Column[] = [];
  public _actionsV2: iAction[] = []; // for row actions

  constructor(private store: Store<any>, private mfaDeviceService: MfaDeviceService, private router: Router) {
    setTimeout(() => {
      this.store.dispatch(new thresholdActions.SetBannerTitleAction('Manage your device'));
      this.store.dispatch(new thresholdActions.SetApplicationTitleAction(''));
      this.store.dispatch(new thresholdActions.ShowBannerAction());
      this.store.dispatch(new thresholdActions.SetColorAction('#fff'));
      this.store.dispatch(new thresholdActions.SetVariantAction('jumbo-variant-dark'));
      this.store.dispatch(new thresholdActions.SetBannerBackgroundAction(''));
      this.store.dispatch(new thresholdActions.SetSizeAction('sol-jumbo-h1'));
      this.store.dispatch(new thresholdActions.HideContextNavAction());
      this.store.dispatch(new thresholdActions.HideMenuAction());
      this.store.dispatch(new thresholdActions.HideProfileBannerAction());
      this.store.dispatch(new thresholdActions.SetDynamicMenuAction([]));

      // Setup menu
      let mainNav: any[] = [
        { title: 'Dashboard', routerPath: '' },
        { title: 'Manage device', routerPath: 'manage-mfa-device' },
      ];
      this.store.dispatch(new thresholdActions.SetMainMenuAction(mainNav));
      this.store.dispatch(new thresholdActions.ShowMainMenuAction());
    });
  }

  ngOnInit(): void {
    this.setupV1Columns();
    this.setupV1Actions();
    this.getV1Devices();

    this.setupV2Columns();
    this.setupV2Actions();
    this.getV2Devices();
  }

  public openRegisterStep1() {
    this.activateDeviceComponentTemplate.openActivateDeviceStep1();
  }

  public setV1TableData(data: Device[]) {
    if (data && data.length) {
      this.getSetDates(data);
      this.deviceEnrolled = true;
    } else {
      this.deviceEnrolled = false;
    }

    this._rows = data;
    this._loading = false;
  }

  public setRows(data: Device[]) {
    this.setV1TableData(data);
  }

  public setV2Rows(data: DeviceV2[]) {
    this._rowsV2 = this.mapV2TableData(data);
    this._loadingV2 = false;
  }

  private getV1Devices() {
    this.loading = true;
    this.warning = false;

    this.mfaDeviceService.getMfaDevice().subscribe(
      (response: Device[]) => {
        if (response && response.length) {
          switch (response[0].status.toLowerCase()) {
            case 'active':
              this.setV1TableData(response);
              break;

            case 'provisioned':
              this.setV1TableData([]);
              break;
          }
        }
        this.loading = false;
      },
      (err: any) => {
        this.loading = false;
        this.warning = true;
      }
    );
  }

  public actionConditionV1(row?: any) {
    if (row.status === 'Active' || row.deviceStatus === 'Active') {
      return true;
    } else {
      return false;
    }
  }

  public actionConditionV2(row?: any) {
    if (row.useMfaQRCodeFlow) {
      return true;
    } else {
      return false;
    }
  }

  public getSetDates(data: Device[]) {
    //Find index of specific object using findIndex method.
    let objRegisteredIndex = data.findIndex((obj) => obj.registeredDateTime === obj.registeredDateTime);
    let objActivityIndex = data.findIndex((obj) => obj.lastActivityDateTime === obj.lastActivityDateTime);

    data[objRegisteredIndex].registeredDateTime = this.formatDate(data[objRegisteredIndex].registeredDateTime);
    data[objActivityIndex].lastActivityDateTime = this.formatDate(data[objActivityIndex].lastActivityDateTime);
  }

  private setupV1Columns(): void {
    this._columns = [
      {
        prop: 'deviceName',
        name: 'Device name',
        display: true,
        alignment: 1,
        cellTemplate: this._deviceNameTemplate,
      },
      {
        prop: 'status',
        name: 'Status',
        display: true,
        alignment: 1,
      },
      {
        prop: 'registeredDateTime',
        name: 'Date registered',
        display: true,
        alignment: 1,
        cellTemplate: this._registeredDateTimeTemplateTemplate,
      },
      {
        prop: 'lastActivityDateTime',
        name: 'Activity',
        display: true,
        alignment: 1,
        cellTemplate: this._lastActivityTemplate,
      },
    ];
  }

  private setupV1Actions(): void {
    this._actions.push(
      {
        title: 'Delink device',
        icon: 'ico-link',
        condition: (row: any) => {
          if (this.delinkFeature) return true;
          else return false;
        },
        command: (row: any) => {
          this.delinkDeviceComponentTemplate.delinkDeviceModal(row);
        },
      },
      {
        title: 'Edit device name',
        icon: 'ico-edit',
        condition: (row: any) => {
          if (this.editFeature) return true;
          else return false;
        },
        command: (row: Device) => {
          this.editDeviceComponentTemplate.editDeviceModal(row);
        },
      },
      {
        title: 'Update app',
        icon: 'ico-edit',
        condition: (row: any) => {
          return true;
        },
        command: (row: Device) => {
          this.router.navigate(['/download-mobile-app']);
        },
      }
    );
  }

  private getV2Devices(): void {
    this.mfaDeviceService.getMfaDeviceV2().subscribe(
      (response: DeviceV2[]) => {
        if (response && response.length) {
          this.setV2Rows(response);
        }
        this._loadingV2 = false;
      },
      (err: any) => {
        this._loadingV2 = false;
      }
    );
  }

  private setupV2Columns(): void {
    this._columnsV2 = [
      {
        prop: 'deviceAlias',
        name: 'Device name',
        display: true,
        alignment: 1,
        cellTemplate: this._deviceNameTemplate,
      },
      {
        prop: 'deviceRegisteredDateTime',
        name: 'Date registered',
        display: true,
        alignment: 1,
        cellTemplate: this._registeredDateTimeTemplateTemplate,
      },
      {
        prop: 'deviceConfirmationDateTime',
        name: 'Date confirmed',
        display: true,
        alignment: 1,
        cellTemplate: this._lastActivityTemplate,
      },
      {
        prop: 'onlineMfaStatus',
        name: 'MFA status old',
        display: false,
        alignment: 1,
      },
      {
        prop: 'isMfaEnabled',
        name: 'MFA status',
        display: true,
        alignment: 1,
        cellTemplate: this._statusTemplate,
      },
      {
        prop: 'isMfaEnabled',
        name: 'Manage MFA',
        display: true,
        alignment: 1,
        cellTemplate: this._enableDisableMfa,
      },
    ];
  }

  private setupV2Actions(): void {
    this._actionsV2.push(
      {
        title: 'Disable MFA',
        icon: 'ico-link',
        condition: (row: any) => {
          // Hide menu if feature flag false, is version 2.0 device with QR code flow(V2.1 device does not use a QR code flow) and mfa is enabled
          if (this.deactivateMfaV2Feature && row.useMfaQRCodeFlow && row.isMfaEnabled === true) return true;
          else return false;
        },
        command: (row: any) => {
          this.delinkDeviceV2ComponentTemplate.delinkDeviceModal(row);
        },
      },
      {
        title: 'Enable MFA',
        icon: 'ico-edit',
        condition: (row: any) => {
          // Hide menu if feature flag false, is version 2.0 device with QR code flow and mfa is disabled
          if (this.activateMfaFeatureV2 && row.useMfaQRCodeFlow && row.isMfaEnabled === false) return true;
          else return false;
        },
        command: (row: DeviceV2) => {
          this.activateDeviceV2ComponentTemplate.openActivateDeviceStep1(row);
        },
      }
    );
  }

  public mapV2TableData(apiResponse: DeviceV2[]): DeviceV2[] {
    if (!apiResponse) {
      return [];
    }
    return apiResponse.map((device) => {
      return {
        ...device,
        deviceRegisteredDateTime: this.formatDate(device.deviceRegisteredDateTime),
        deviceConfirmationDateTime: this.formatDate(device.deviceConfirmationDateTime),
      };
    });
  }

  public enableDisableV2DeviceMfa(row: DeviceV2) {
    this.activateDeviceV2ComponentTemplate.openEnableDisableDevice(row);
  }

  private formatDate(date: string): string {
    if (!date) {
      return '-';
    }

    return new Date(date).toLocaleString('en-GB', { hour12: false });
  }
}



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./~/angular-router-loader/src!./src/app/threshold/components/manage-mfa-device/manage-mfa-device-component.ts